import { useParams, useNavigate } from "@/lib/router-compat";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ContentPageNav } from "@/components/ContentPageNav";
import { Footer } from "@/components/Footer";
import { ArrowRight, ArrowLeft, Share2 } from "lucide-react";
import { newsArticles } from "./Nieuws";

const ShareButton = ({ icon, label, href }: { icon: React.ReactNode; label: string; href: string }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted transition-colors"
  >
    {icon}
    {label}
  </a>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const InstagramIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z" />
  </svg>
);

const LaunchArticle = () => {
  const navigate = useNavigate();
  const pageUrl = "https://dehusselapp.nl/nieuws/de-hussel-app-is-live";
  const shareText = "De Hussel App is live! Automatiseer je hussel- en tossavonden. 🎾";

  const shareLinks = {
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + " " + pageUrl)}`,
    instagram: `https://www.instagram.com/direct/new/?text=${encodeURIComponent(shareText + " " + pageUrl)}`,
  };

  return (
    <>
      {/* JSON-LD structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "headline": "De Hussel App is live — het tennisseizoen kan beginnen!",
        "datePublished": "2026-02-26",
        "author": { "@type": "Organization", "name": "De Hussel App" },
        "publisher": { "@type": "Organization", "name": "De Hussel App" },
        "description": "De Hussel App lanceert: automatiseer hussel-, toss- en racket-trekken-avonden voor tennis- en padelclubs. Vanaf €0,07 per lid per maand.",
        "mainEntityOfPage": pageUrl,
      })}} />

      <article className="prose prose-lg dark:prose-invert max-w-none">
        <time className="text-sm text-muted-foreground not-prose">26 februari 2026</time>
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-6">
          De Hussel App is live — het tennisseizoen kan beginnen! 🎾
        </h1>

        <p className="text-lg text-foreground leading-relaxed">
          De banen liggen er strak bij, de eerste zonnestralen verwarmen het gravel en overal in het land worden de netten
          gespannen. Het nieuwe tennisseizoen staat voor de deur — en dit jaar wordt alles anders.
          <strong> De Hussel App is officieel gelanceerd!</strong>
        </p>

        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4">Iedereen is weer opgeladen</h2>
        <p className="text-foreground leading-relaxed">
          Na een lange winter vol indoor-sessies en trainingsuren popelen spelers om weer de buitenbanen op te gaan.
          De energie is voelbaar: nieuwe ballen, schone griplint en die onmisbare geur van vers gravel. Het is dé tijd
          om je club weer tot leven te brengen met gezellige hussel- en tossavonden.
        </p>

        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4">Wat is De Hussel App?</h2>
        <p className="text-foreground leading-relaxed">
          De Hussel App is een slimme tool die <strong>hussel-avonden, toss-momenten en racket-trekken</strong> volledig
          automatiseert. Geen vrijwilligerscommissie meer nodig, geen briefjes of lijsten, en geen gedoe bij het
          clubhuis. Alles draait automatisch via de telefoon van je leden.
        </p>

        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4">De belangrijkste features</h2>
        <ul className="space-y-2 text-foreground">
          <li>🔀 <strong>Automatische indeling</strong> — Slimme algoritmes verdelen spelers eerlijk over de banen op basis van niveau en gespeelde rondes</li>
          <li>📱 <strong>Aanmelden via mobiel</strong> — Geen pasjes of lijsten meer, leden melden zich aan op hun telefoon</li>
          <li>⏱️ <strong>Realtime overzicht</strong> — Spelers zien direct op hun scherm met wie ze spelen en op welke baan</li>
          <li>🔔 <strong>Push notificaties</strong> — Automatische meldingen bij nieuwe rondes of wijzigingen</li>
          <li>👥 <strong>Geen vrijwilligers nodig</strong> — Het systeem draait volledig zelfstandig</li>
          <li>🏢 <strong>Club branding</strong> — White-label met jouw clublogo en naam</li>
          <li>🎾 <strong>Tennis én padel</strong> — Geschikt voor beide sporten</li>
          <li>🔄 <strong>Drie speelvormen</strong> — Husselen, tossen én racket-trekken</li>
        </ul>

        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4">Wat kost het?</h2>
        <p className="text-foreground leading-relaxed">
          De Hussel App werkt met een eenvoudig club-abonnement. Geen kosten per speler, geen verborgen kosten. Je club
          betaalt één vast bedrag per maand, ongeacht hoe vaak je speelt.
        </p>
        <div className="not-prose my-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-border bg-card/50 p-4 text-center">
            <p className="text-sm text-muted-foreground">Kleine club (t/m 100 leden)</p>
            <p className="text-2xl font-bold text-foreground mt-1">€19,95<span className="text-sm font-normal text-muted-foreground">/mnd</span></p>
            <p className="text-xs text-muted-foreground">excl. BTW</p>
          </div>
          <div className="rounded-lg border-2 border-accent bg-accent/10 p-4 text-center">
            <Badge className="mb-1">Populair</Badge>
            <p className="text-sm text-muted-foreground">Gemiddelde club (101-500)</p>
            <p className="text-2xl font-bold text-foreground mt-1">€24,95<span className="text-sm font-normal text-muted-foreground">/mnd</span></p>
            <p className="text-xs text-muted-foreground">excl. BTW</p>
          </div>
          <div className="rounded-lg border border-border bg-card/50 p-4 text-center">
            <p className="text-sm text-muted-foreground">Grote club (500+)</p>
            <p className="text-2xl font-bold text-foreground mt-1">€34,95<span className="text-sm font-normal text-muted-foreground">/mnd</span></p>
            <p className="text-xs text-muted-foreground">excl. BTW</p>
          </div>
        </div>
        <p className="text-foreground leading-relaxed">
          Bij het grootste abonnement voor clubs met 500+ leden kost De Hussel App <strong>vanaf slechts €0,07
          per lid per maand</strong>. Vergelijk dat met de uren die een husselcommissie wekelijks kwijt is!
        </p>

        <h2 className="text-2xl font-bold text-foreground mt-10 mb-4">Klaar voor het nieuwe seizoen?</h2>
        <p className="text-foreground leading-relaxed mb-6">
          Het enige wat je hoeft te doen is je club aanmelden, de banen configureren en de link delen met je leden.
          De Hussel App doet de rest. Geen commissie, geen gedoe — gewoon spelen.
        </p>

        <div className="not-prose flex flex-col sm:flex-row gap-4 mb-10">
          <Button
            size="lg"
            className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground"
            onClick={() => navigate("/club-register")}
          >
            Club aanmelden <ArrowRight className="h-5 w-5" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate("/demo")}
          >
            Bekijk de demo
          </Button>
        </div>

        {/* Share buttons */}
        <div className="not-prose border-t border-border pt-6 mt-10">
          <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
            <Share2 className="h-4 w-4" /> Deel dit artikel
          </p>
          <div className="flex flex-wrap gap-2">
            <ShareButton icon={<XIcon />} label="X" href={shareLinks.x} />
            <ShareButton icon={<FacebookIcon />} label="Facebook" href={shareLinks.facebook} />
            <ShareButton icon={<WhatsAppIcon />} label="WhatsApp" href={shareLinks.whatsapp} />
            <ShareButton icon={<InstagramIcon />} label="Instagram" href={shareLinks.instagram} />
          </div>
        </div>
      </article>
    </>
  );
};

// Article registry
const articleComponents: Record<string, React.FC> = {
  "de-hussel-app-is-live": LaunchArticle,
};

const NewsArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const article = newsArticles.find((a) => a.slug === slug);
  const ArticleComponent = slug ? articleComponents[slug] : undefined;

  if (!article || !ArticleComponent) {
    navigate("/nieuws");
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <ContentPageNav showPricing={false} />

      <main className="container mx-auto max-w-3xl px-4 py-12 md:py-20">
        <button
          onClick={() => navigate("/nieuws")}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Terug naar nieuws
        </button>

        <ArticleComponent />
      </main>

      <Footer />
    </div>
  );
};

export default NewsArticlePage;
